import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { GET_LAWYER_CASES_LIST } from '@/api';
import { EmptyCases } from './EmptyCases';
import { LawyerCasesGridScreen } from './CasesGrid';
import { FiltersLawyer } from '@/components/dashboard';
import { LoaderScreen, PaginationCustom, Tabs } from '@/components/common';
import { casesLawyerService } from '@/api/casesLawyer';
import { EmptyStateFilter } from '../common/EmptyStateFilter';
import { useEventSource } from '@/hooks/useEventSource';
import Icons from '@/const/icons';
import { Allowed } from '@/components/common/allowed/Allowed';
import { useArchivedCases } from '@/hooks';
import { CasesTabName } from '@/interfaces';

const casesTabs: { name: CasesTabName; label: string }[] = [
	{ name: 'my-cases', label: 'My Cases' },
	{ name: 'archived', label: 'Archived' },
];

export const LawyerCasesScreen: React.FC = () => {
	const router = useRouter();

	const { page, search, status, phase, alphabetical } = router.query;
	const currentPage = page ?? '1';

	const [currentTab, setCurrentTab] = React.useState<CasesTabName>('my-cases');

	const totalRecords = React.useRef(null);

	const { isPending, archiveCase, unarchiveCase } = useArchivedCases();

	const { data: stepNotification } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/action-resolved/subscribe`,
	});

	const firstParsed = stepNotification ? JSON.parse(stepNotification) : null;
	const parsedNotification = firstParsed ? JSON.parse(firstParsed.data) : null;
	const notDate = parsedNotification?.date;

	const {
		data: casesRes,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			GET_LAWYER_CASES_LIST,
			search,
			status,
			phase,
			alphabetical,
			page,
			currentTab,
		],
		queryFn: () =>
			casesLawyerService.get({
				page: currentPage as string,
				phase: phase ? (phase as string).split('-')[0] : null,
				search: search as string,
				status: status ? (status as string).split('-')[0] : null,
				alphabetical: !!alphabetical,
				limit: 16,
				archived: currentTab === 'archived',
			}),
	});

	React.useEffect(() => {
		if (totalRecords.current === null && casesRes) {
			(totalRecords.current as number | null) = casesRes.data.totalItems;
		}
	}, [casesRes]);

	const visibleListCases = casesRes?.data.items || [];
	const totalItems = casesRes?.data.totalItems ?? 0;
	const totalPages = casesRes?.data.totalPages ?? 1;
	const total = visibleListCases.length;

	const isFilterApplied = !!status || !!phase || !!alphabetical;
	const isSearch = !!search;

	React.useEffect(() => {
		if (notDate) {
			refetch();
		}
	}, [notDate]);

	const handleTabChange = (value: string) => {
		setCurrentTab(value as CasesTabName);
		if (currentPage !== '1') {
			router.replace(
				{ pathname: router.pathname, query: { ...router.query, page: '1' } },
				undefined,
				{ shallow: true }
			);
		}
	};

	return (
		<div className={clsx('flex flex-col space-y-6')}>
			<FiltersLawyer
				total={total}
				totalItems={totalItems}
				totalRecords={totalRecords.current}
				sizeText="regular-14"
			/>

			<Tabs
				dataCY="cases"
				optionsTabs={casesTabs}
				currentOption={currentTab}
				setCurrentOption={handleTabChange}
			/>

			<Allowed need={'case_admin'}>
				{currentTab === 'my-cases' &&
					!isLoading &&
					visibleListCases.length === 0 &&
					!isFilterApplied &&
					!isSearch && <EmptyCases />}
			</Allowed>

			{isLoading && <LoaderScreen />}

			{!isLoading && visibleListCases.length > 0 && (
				<div className="flex flex-col justify-between h-full">
					<LawyerCasesGridScreen
						listCases={visibleListCases}
						isArchived={currentTab === 'archived'}
						isArchivePending={isPending}
						onArchive={archiveCase}
						onUnarchive={unarchiveCase}
					/>
					{totalPages && totalPages > 1 ? (
						<PaginationCustom totalPages={totalPages} />
					) : null}
				</div>
			)}

			{!isLoading &&
				visibleListCases.length === 0 &&
				isSearch &&
				!isFilterApplied && (
					<div className="pt-[125px]">
						<EmptyStateFilter
							icon={Icons.searchCaseEmptyState}
							iconClassName="!w-[195px] h-[150px]"
							message={'No results were found for your search.'}
							subMessage={'Please try using different keywords.'}
						/>
					</div>
				)}

			{!isLoading && visibleListCases.length === 0 && isFilterApplied && (
				<div className="pt-[125px]">
					<EmptyStateFilter
						icon={Icons.filterEmptyState}
						iconClassName="w-[175px] h-[140px]"
						message={'There are currently no cases matching this filter.'}
						subMessage={'Please try adjusting your filter.'}
					/>
				</div>
			)}

			{!isLoading &&
				visibleListCases.length === 0 &&
				!isSearch &&
				!isFilterApplied &&
				currentTab === 'archived' && (
					<div className="pt-[125px]">
						<EmptyStateFilter
							icon={Icons.emptyStateBox}
							iconClassName="w-[175px] h-[140px]"
							message={'No archived cases yet'}
							subMessage={
								'Cases you archive will appear here for historical reference.'
							}
							subMessageClassName="!text-[#6E6A73]"
						/>
					</div>
				)}
		</div>
	);
};
